import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import api from '../../api/api'
import { Gift, Copy, Check, Share2, Twitter, MessageCircle } from 'lucide-react'
import toast from 'react-hot-toast'

// Generate a human-readable slug from a user's name + code suffix
const makeSlug = (name, code) => {
    const namePart = (name || 'user')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .substring(0, 20)
    const suffix = (code || '').substring(0, 6)
    return `${namePart}-${suffix}`.toLowerCase()
}

export default function Referral() {
    const { userInfo } = useSelector(s => s.auth)
    const [data, setData] = useState(null)
    const [copied, setCopied] = useState(false)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        api.get('/customer/referral', { withCredentials: true })
            .then(res => setData(res.data))
            .catch(() => toast.error('Failed to load referral info'))
            .finally(() => setLoading(false))
    }, [])

    // Human-readable share link shown to user
    const slug = data?.referralCode ? makeSlug(userInfo?.name, data.referralCode) : ''
    // Actual functional link using the real code
    const referralLink = data?.referralCode
        ? `${window.location.origin}/register?ref=${data.referralCode}`
        : ''
    // Display-friendly version
    const displayLink = data?.referralCode
        ? `${window.location.origin}/join/${slug}`
        : ''

    const handleCopy = () => {
        if (!referralLink) return
        navigator.clipboard.writeText(referralLink).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }

    const shareText = `Join me on Omah Global and get a welcome bonus! Use my link: ${referralLink}`
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`

    if (loading) {
        return (
            <div className="py-12 text-center text-gray-400 text-sm">Loading...</div>
        )
    }

    return (
        <div className="p-4 max-w-2xl">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                <Gift size={20} className="text-indigo-600" />
                Referral Program
            </h2>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Referral Wallet</p>
                    <p className="text-2xl font-bold text-green-600">${(data?.referralWallet || 0).toFixed(2)}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">People Referred</p>
                    <p className="text-2xl font-bold text-gray-800">{data?.referralCount || 0}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm col-span-2 md:col-span-1">
                    <p className="text-xs text-gray-500 mb-1">Your Code</p>
                    <p className="text-xl font-bold font-mono text-indigo-600">{data?.referralCode || '—'}</p>
                </div>
            </div>

            {/* Share Link */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Your Referral Link</h3>
                <p className="text-xs text-gray-500 mb-3">
                    Share this link. When someone signs up through it, you both earn a bonus automatically.
                </p>
                {/* Display-friendly slug link */}
                <p className="text-[11px] font-mono text-gray-400 mb-2 truncate">{displayLink}</p>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={referralLink}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-600 font-mono bg-gray-50 focus:outline-none"
                    />
                    <button
                        onClick={handleCopy}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${copied ? 'bg-green-100 text-green-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                    >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                </div>
            </div>

            {/* Social Share */}
            <div className="flex gap-2 mb-6">
                <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-green-500 text-white hover:bg-green-600 transition-colors"
                >
                    <MessageCircle size={14} /> WhatsApp
                </a>
                <a
                    href={twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-sky-500 text-white hover:bg-sky-600 transition-colors"
                >
                    <Twitter size={14} /> Twitter/X
                </a>
                <button
                    onClick={() => {
                        if (navigator.share) {
                            navigator.share({ title: 'Join Omah Global', text: shareText, url: referralLink })
                        } else {
                            handleCopy()
                        }
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-gray-800 text-white hover:bg-gray-700 transition-colors"
                >
                    <Share2 size={14} /> Share
                </button>
            </div>

            {/* How it works */}
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100">
                <h3 className="text-sm font-semibold text-indigo-800 mb-3">How It Works</h3>
                <ol className="list-decimal list-inside space-y-1.5 text-xs text-indigo-700">
                    <li>Share your referral link or code with friends</li>
                    <li>They sign up on Omah Global using your link</li>
                    <li>You both instantly receive a bonus in your referral wallets</li>
                    <li>Contact support to request a payout when you hit the minimum threshold</li>
                </ol>
            </div>
        </div>
    )
}
